import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';

import { RequireCapability } from '@/components/layout/RequireCapability';
import { CopyableId } from '@/components/primitives/CopyableId';
import { LiveDataIndicator } from '@/components/primitives/LiveDataIndicator';
import { StateChip } from '@/components/primitives/StateChip';
import { ErrorState } from '@/components/states/ErrorState';
import { Skeleton } from '@/components/states/Skeleton';
import { ISSUE_LABELS, useLeagueDetail } from '@/features/leagues/leagues';
import { recordRecentEntity } from '@/features/recents/recent-entities';

export const Route = createFileRoute('/leagues/$leagueId')({
  component: LeagueDetailLayout,
});

/**
 * Tabs for a single league. Order follows the brief's section 5.2, with Rosters
 * early because it is the screen the on-call engineer opens most.
 */
const TABS: { to: string; label: string; exact?: boolean }[] = [
  { to: '.', label: 'Overview', exact: true },
  { to: 'members', label: 'Members' },
  { to: 'rosters', label: 'Rosters' },
  { to: 'draft', label: 'Draft' },
  { to: 'weeks', label: 'Weeks' },
  { to: 'waivers', label: 'Waivers' },
  { to: 'trades', label: 'Trades' },
  { to: 'playoffs', label: 'Playoffs' },
  { to: 'philanthropy', label: 'Philanthropy' },
  { to: 'activity', label: 'Activity' },
];

function LeagueDetailLayout() {
  const { leagueId } = Route.useParams();
  const { data, isPending, isFetching, isError, error, refetch, dataUpdatedAt } =
    useLeagueDetail(leagueId);

  // Feeds the sidebar's "Recent" list so an operator can jump back mid-incident.
  useEffect(() => {
    if (!data) return;
    recordRecentEntity({
      kind: 'league',
      id: data.id,
      name: data.name,
      to: `/leagues/${data.id}`,
    });
  }, [data]);

  return (
    <RequireCapability capability="leagues.view">
      <div className="flex flex-col gap-3.5">
        {/*
          Persistent header. Stays visible across every tab so the operator never loses
          sight of week status and lock state while acting — that context is what makes
          a Tier 3 confirmation meaningful rather than a formality.
        */}
        <div
          className="border-bd bg-card sticky top-[59px] z-50 rounded-xl border p-3.5 backdrop-blur-[12px]"
          style={{ background: 'var(--chrome-bg)' }}
        >
          {isError ? (
            <ErrorState error={error} subject="this league" onRetry={() => void refetch()} />
          ) : isPending ? (
            <div className="flex flex-col gap-2">
              <Skeleton height={22} width="42%" />
              <div className="flex gap-2">
                <Skeleton height={20} width={96} className="rounded-full" />
                <Skeleton height={20} width={80} className="rounded-full" />
                <Skeleton height={20} width={110} className="rounded-full" />
              </div>
            </div>
          ) : data ? (
            <>
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                <div className="flex min-w-0 items-center gap-2.5">
                  {data.imageUrl ? (
                    <img
                      src={data.imageUrl}
                      alt=""
                      className="border-bd size-9 flex-none rounded-lg border object-cover"
                    />
                  ) : (
                    <span
                      aria-hidden
                      className="grid size-9 flex-none place-items-center rounded-lg font-mono text-[11px] font-bold"
                      style={{
                        background: 'rgba(var(--brand-rgb), 0.22)',
                        color: 'var(--brand-soft)',
                      }}
                    >
                      LG
                    </span>
                  )}
                  <div className="min-w-0">
                    <h1 className="font-head text-tx truncate text-[22px] leading-none font-semibold">
                      {data.name}
                    </h1>
                    <div className="text-tx3 mt-1 flex flex-wrap items-center gap-x-2 text-[11.5px] capitalize">
                      <span>{data.sport ?? 'no sport'}</span>
                      <span aria-hidden>·</span>
                      <span>{data.leagueType ?? 'squad'}</span>
                      <span aria-hidden>·</span>
                      <span className="mesh-mono">
                        {data.filledTeams} / {data.leagueSize ?? '?'} teams
                      </span>
                      <span aria-hidden>·</span>
                      <span className="mesh-mono">{data.memberCount} users</span>
                      {data.isSimulationLeague && (
                        <>
                          <span aria-hidden>·</span>
                          <span className="text-epic">simulation</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <LiveDataIndicator
                  updatedAt={dataUpdatedAt || undefined}
                  isFetching={isFetching}
                  isLive
                  // Tight threshold: this header is what a Tier 3 decision is read from.
                  staleAfterMs={45_000}
                  onRefresh={() => void refetch()}
                />
              </div>

              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                {data.currentWeek?.status && (
                  <StateChip
                    state={data.currentWeek.status}
                    label={`Week ${String(data.currentWeek.week ?? '?')} · ${data.currentWeek.status}`}
                  />
                )}
                {data.season && <StateChip state={data.season.status} />}
                {data.shareCode && <CopyableId value={data.shareCode} label="Invite code" />}
                <CopyableId value={data.id} label="League ID" truncate />
              </div>

              {/* Lock time is stated in absolute terms — "in 2 hours" is ambiguous
                  across the timezone the league actually runs in. */}
              {data.currentWeek?.lockAt && (
                <div className="mesh-mono text-tx3 mt-2 text-[11px]">
                  locks {data.currentWeek.lockAt.replace('T', ' ').slice(0, 16)}Z
                  {data.timeZone ? ` · league tz ${data.timeZone}` : ''}
                </div>
              )}

              {data.issues.length > 0 && (
                <div
                  className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-2.5 py-2"
                  style={{
                    borderColor: 'rgba(var(--err-rgb), 0.36)',
                    background: 'rgba(var(--err-rgb), 0.07)',
                  }}
                >
                  <span
                    className="font-head text-err-soft text-[10.5px] font-semibold uppercase"
                    style={{ letterSpacing: '0.14em' }}
                  >
                    Open issues
                  </span>
                  {data.issues.map((issue) => (
                    <span key={issue} className="text-err-soft text-[12px]">
                      {ISSUE_LABELS[issue].label}
                    </span>
                  ))}
                </div>
              )}

              {data.currentWeek?.lastLifecycleError && (
                <pre className="border-bd bg-bg-alt text-tx2 mt-2 overflow-x-auto rounded-lg border p-2 font-mono text-[11px] whitespace-pre-wrap">
                  {data.currentWeek.lastLifecycleStep
                    ? `${data.currentWeek.lastLifecycleStep}: `
                    : ''}
                  {data.currentWeek.lastLifecycleError}
                </pre>
              )}
            </>
          ) : null}
        </div>

        <nav
          aria-label="League sections"
          className="-mx-3.5 flex gap-1 overflow-x-auto px-3.5 pb-1 md:mx-0 md:px-0"
        >
          {TABS.map((tab) => (
            <Link
              key={tab.label}
              from={Route.fullPath}
              to={tab.to}
              activeOptions={{ exact: tab.exact ?? false }}
              className="font-head border-bd text-tx3 hover:bg-elev hover:text-tx flex-none rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold tracking-[0.08em] uppercase no-underline hover:no-underline"
              activeProps={{
                style: {
                  background: 'var(--high)',
                  borderColor: 'var(--bd-s)',
                  color: 'var(--tx)',
                },
              }}
            >
              {tab.label}
            </Link>
          ))}
        </nav>

        <Outlet />
      </div>
    </RequireCapability>
  );
}
